import React from "react";

import CreatableSelect from "react-select/creatable";
import Select from "react-select";
import {getPrimaryColor} from "@/utils/theme";

const VLFSelectNew = ({
	options,
	value,
	onChange,
	label,
	placeholder = "Seleziona...",
	isMulti = false,
	isClearable = true,
	isDisabled = false,
	required = false,
	editable = true,
	sorted = true,
	sortOrder = "ascend",
	sortKey = "label",
	error = false,
	onCreateOption,
	styles = {},
	containerStyles = {},
	controlStyles = {},
	theme = {},
	tableField = false,
	noMargin = false,
	...props
}) => {
	const Component = onCreateOption ? CreatableSelect : Select;
	const sortedOptions = sorted
		? options
				?.slice()
				.sort((a, b) =>
					sortOrder === "ascend"
						? a[sortKey]?.toString().localeCompare(b[sortKey])
						: b[sortKey]?.toString().localeCompare(a[sortKey]),
				)
		: options;

	// Get theme colors
	const primaryColor = getPrimaryColor();

	return (
		<>
			{label && (
				<div className="field-label-container">
					<div className="field-label">{label}</div>
					{required && editable && <div className="field-required">*</div>}
				</div>
			)}
			{editable ? (
				<>
					<Component
						options={sortedOptions}
						value={value}
						onChange={onChange}
						onCreateOption={onCreateOption}
						placeholder={placeholder}
						isMulti={isMulti}
						isClearable={isClearable}
						isDisabled={isDisabled}
						menuPortalTarget={document.body}
						styles={{
							control: (provided, state) => ({
								...provided,
								marginTop: "0px",
								marginBottom: tableField || noMargin ? "0px" : "8px",
								height: tableField ? "38px" : !isMulti ? "42px" : "100%",
								minHeight: tableField ? "38px" : !isMulti ? "42px" : "100%",
								fontSize: tableField ? "12px" : "14px",
								width: tableField ? "200px" : "100%",
								...controlStyles,
								// Come i campi di testo: fondo pieno, bordo sottile, alone al focus
								background: "var(--color-surface)",
								borderRadius: "var(--radius)",
								borderColor: error
									? "var(--color-critical)"
									: state.isFocused
										? primaryColor
										: "var(--color-text-muted)",
								borderWidth: "1px",
								boxShadow: state.isFocused
									? "0 0 0 3px var(--color-primary-alpha-20)"
									: "none",
								transition: "border-color 0.15s ease, box-shadow 0.15s ease",
								"&:hover": {
									borderColor: error
										? "var(--color-critical)"
										: state.isFocused
											? primaryColor
											: "var(--color-text-secondary)",
								},
								outline: "none",
								"&:focus-visible": {
									outline: "none",
									boxShadow: state.isFocused
										? "0 0 0 3px var(--color-primary-alpha-20)"
										: "none",
								},
								cursor: isDisabled ? "not-allowed" : "pointer",
								opacity: isDisabled ? 0.7 : 1,
							}),
							container: (provided) => ({
								...provided,
								width: "100%",
								...containerStyles,
							}),
							valueContainer: (provided) => ({
								...provided,
								padding: "0 14px",
							}),
							input: (provided) => ({
								...provided,
								margin: "0px",
								padding: "0px",
								color: "var(--color-text)",
								fontWeight: 400,
								letterSpacing: "0.01em",
							}),
							placeholder: (provided) => ({
								...provided,
								color: "var(--color-text-muted)",
								opacity: 0.7,
							}),
							singleValue: (provided) => ({
								...provided,
								color: "var(--color-text)",
								fontWeight: 400,
								letterSpacing: "0.01em",
							}),
							clearIndicator: (provided) => ({
								...provided,
								color: "var(--color-text-secondary)",
								padding: "8px",
								transition: "color 0.2s ease",
								"&:hover": {
									color: "var(--color-primary)",
								},
							}),
							dropdownIndicator: (provided, state) => ({
								...provided,
								color: state.isFocused
									? "var(--color-primary)"
									: "var(--color-text-secondary)",
								padding: "8px",
								transition: "color 0.2s ease",
								"&:hover": {
									color: "var(--color-primary)",
								},
							}),
							option: (styles, {data, isFocused, isSelected}) => {
								return {
									...styles,
									color: data.color || "var(--color-text)",
									backgroundColor: isSelected
										? "var(--color-primary-alpha-12)"
										: isFocused
											? "var(--color-primary-alpha-5)"
											: "transparent",
									fontWeight: isSelected ? 600 : 400,
									cursor: "pointer",
									transition: "all 0.2s ease",
									"&:active": {
										backgroundColor: "var(--color-primary-alpha-15)",
									},
								};
							},
							menu: (provided) => ({
								...provided,
								zIndex: 9999,
								borderRadius: "var(--radius)",
								boxShadow:
									"0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)",
								border: "1px solid var(--color-border)",
								background: "var(--color-surface)",
								marginTop: "4px",
							}),
							menuPortal: (provided) => ({
								...provided,
								zIndex: 9999,
							}),
							multiValue: (styles) => ({
								...styles,
								backgroundColor: "var(--color-primary)",
								color: "var(--color-on-accent)",
								borderRadius: "var(--radius-pill)",
							}),
							multiValueLabel: (styles) => ({
								...styles,
								color: "var(--color-on-accent)",
							}),
							multiValueRemove: (styles) => ({
								...styles,
								color: "var(--color-on-accent)",
								":hover": {
									backgroundColor: "var(--color-primary-dark)",
								},
							}),
							...styles,
						}}
						menuPlacement={"auto"}
						theme={(defaultTheme) => ({
							...defaultTheme,
							...theme,
						})}
						{...props}
					/>
					{error && (
						<p
							className="MuiFormHelperText-root Mui-error"
							style={{color: "var(--color-critical)"}}
						>
							Campo obbligatorio
						</p>
					)}
				</>
			) : (
				<div className="field-value">{value?.label ?? value ?? "---"}</div>
			)}
		</>
	);
};

export default VLFSelectNew;
